import { createSlice } from '@reduxjs/toolkit';

// In-browser project bank downloads run as a background archive job on the
// backend. The job is tracked here rather than in the scene that started it,
// so the user can move around the app while it builds: the progress
// snackbar reads from this slice, and the result is offered wherever they
// are when the job finishes — the download itself. A PROJECT_EMAIL job runs
// without snackbars: the user writes its email in the project email dialog
// and the worker sends it. Finished jobs stay in the map: the dialog reads
// their status and fills the archive's name, count and size into the
// message. Links emailed by the worker from the recipients dialog are not
// tracked.
const initialState = {
  // Keyed by archiveJobId.
  jobs: {},
};

export const projectBankArchiveJobsSlice = createSlice({
  name: 'projectBankArchiveJobs',
  initialState,
  reducers: {
    archiveJobStarted(state, action) {
      const job = action.payload;
      state.jobs[job.archiveJobId] = {
        status: 'RUNNING',
        // DOWNLOAD: the browser downloads the finished archive.
        // PROJECT_EMAIL: the user writes a project email for its link, and
        // the worker sends it when the archive is ready.
        delivery: 'DOWNLOAD',
        // PROJECT_EMAIL: the message has been written and handed to the
        // backend, which sends it the moment the archive is ready.
        emailScheduled: false,
        // Worker stages: COLLECTING, BUILDING (files), ASSEMBLING (parts of
        // the final object, parallel builds only).
        stage: 'COLLECTING',
        doneFiles: 0,
        totalFiles: 0,
        // Exact, from the worker, once it has resolved the entries.
        totalBytes: null,
        doneParts: 0,
        totalParts: 0,
        result: null,
        ...job,
      };
    },
    archiveJobProgress(state, action) {
      const { archiveJobId, ...progress } = action.payload;
      const job = state.jobs[archiveJobId];
      if (job) {
        Object.assign(job, progress);
      }
    },
    archiveJobFinished(state, action) {
      const { archiveJobId, status, result } = action.payload;
      const job = state.jobs[archiveJobId];
      if (job) {
        job.status = status;
        job.result = result || null;
      }
    },
    archiveJobEmailScheduled(state, action) {
      const job = state.jobs[action.payload];
      if (job) {
        job.emailScheduled = true;
      }
    },
  },
});

export const {
  archiveJobStarted,
  archiveJobProgress,
  archiveJobFinished,
  archiveJobEmailScheduled,
} = projectBankArchiveJobsSlice.actions;
